/**
 * Analytics Portal V1 — the page shell (Phase 9, plan §12.1).
 *
 * Owns everything every dashboard shares: heading, tab bar, entity selector, category filter,
 * date range, comparison toggle, refresh, freshness, timezone display, archived indicator,
 * availability notices, and the loading / error / empty / locked states. It knows **nothing**
 * about any specific metric — Phases 10–15 fill the body through `children`.
 */
import React from "react";
import { Globe2 } from "lucide-react";
import { TAB_ENTITY_TYPE } from "@/lib/analyticsApi";
import { AvailabilityNotice } from "./AvailabilityNotice";
import { ArchivedNotice, CategoryFilter, ComparisonToggle, RefreshButton } from "./Controls";
import { DateRangeSelector } from "./DateRangeSelector";
import { EntitySelector } from "./EntitySelector";
import { ExportMenu } from "./ExportMenu";
import { FreshnessIndicator } from "./FreshnessIndicator";

export const AnalyticsShell = ({
  config, tab, tabs = [], onTabChange, entities = [], entitiesLoading, entity, onEntityChange,
  showEntitySelector = true, showFilters = true,
  category, onCategoryChange, counts, range, rangeLabel, comparisonLabel, onPreset,
  onCustomRange, onCompare, onRefresh, refreshing, freshness, payload, children,
}) => {
  const isSummary = tab === "summary";
  const timezone = payload?.reporting?.reporting_timezone || config?.reporting_timezone;
  const notices = payload?.notices || [];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pb-16 pt-6 sm:px-6 lg:px-8"
         data-testid="analytics-portal">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-[720] tracking-tight text-foreground sm:text-4xl">
            Analytics
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who found you, what they did, and what changed.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <FreshnessIndicator freshness={freshness} timezone={timezone} />
          <RefreshButton onRefresh={onRefresh} busy={refreshing} />
          {/* Phase 18: single-entity exports only — never on Summary, never without a payload. */}
          {!isSummary && payload?.entity && (
            <ExportMenu entityType={payload.entity.type || TAB_ENTITY_TYPE[tab]}
                        entityId={payload.entity.id || entity}
                        capabilities={payload.capabilities} range={range} timezone={timezone} />
          )}
        </div>
      </header>

      <nav className="-mx-4 mt-6 overflow-x-auto px-4 sm:mx-0 sm:px-0" aria-label="Analytics sections">
        <ul className="flex min-w-max gap-1.5" role="tablist">
          {tabs.map((option) => {
            const active = option.key === tab;
            return (
              <li key={option.key} role="presentation">
                <button
                  type="button" role="tab" aria-selected={active}
                  aria-current={active ? "page" : undefined}
                  onClick={() => onTabChange(option.key)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-[560] transition-colors ${
                    active ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
                  data-testid={`analytics-tab-${option.key}`}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {showFilters && (
      <section className="mt-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
               aria-label="Analytics filters">
        <div className="flex flex-wrap items-center gap-2">
          {!isSummary && showEntitySelector && (
            <EntitySelector entities={entities} value={entity} onChange={onEntityChange}
                            loading={entitiesLoading} />
          )}
          {isSummary && (
            <CategoryFilter value={category} onChange={onCategoryChange} counts={counts} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangeSelector presets={config?.presets || []} preset={range?.preset}
                             start={range?.start} end={range?.end} rangeLabel={rangeLabel}
                             onPreset={onPreset} onCustomRange={onCustomRange} />
          <ComparisonToggle checked={range?.compare !== false} onChange={onCompare}
                            comparisonLabel={comparisonLabel} />
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground"
                data-testid="analytics-timezone">
            <Globe2 className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">Times shown in </span>{timezone || "UTC"}
          </span>
        </div>
      </section>
      )}

      {(payload?.entity || notices.length > 0) && (
        <div className="mt-4 space-y-2">
          <ArchivedNotice entity={payload?.entity} />
          {notices.filter((notice) => notice.code !== "entity_archived").map((notice) => (
            <AvailabilityNotice key={notice.code} notice={notice.message} reason={notice.code}
                                testId={`analytics-notice-${notice.code}`} />
          ))}
        </div>
      )}

      <div className="mt-5" aria-live="polite">{children}</div>
    </div>
  );
};

export const entityTypeForTab = (tab) => TAB_ENTITY_TYPE[tab] || null;
